import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { globalAction } from 'common/actions';
import { ERROR_404, ERROR_504 } from 'common/config/api';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import UnsavedChangesContext from 'context/UnsavedChangesContext';
import { MAX_ATTEMPTS, POLL_INTERVAL } from 'services/hooks/useCheckReportFile/constants';
import { OptionalStatus } from 'services/hooks/useCheckReportFile/useCheckReportFile.props';
import { useResponse } from 'services/hooks/useResponse';

export function useCheckReportFile<TRequestData, TPromiseType extends OptionalStatus, SPromiseType>({
  pollingFn,
  onSuccessFn,
  defaultErrorMessage,
  successMessage,
  maxAttemptsMessage,
  keepPollingStatusCodes,
  infoMessage,
  maxAttempts = MAX_ATTEMPTS,
  pollInterval = POLL_INTERVAL,
}: {
  keepPollingStatusCodes: number[];
  pollingFn: (reportUUID: string, requestData: TRequestData) => Promise<TPromiseType>;
  successMessage: string;
  defaultErrorMessage: string;
  maxAttemptsMessage: string;
  infoMessage: string;
  maxAttempts?: number;
  pollInterval?: number;
  onSuccessFn: (onSuccessData: TPromiseType, requestData: TRequestData) => Promise<SPromiseType> | void;
}) {
  const [, dispatch] = useStore() as unknown as UseStoreValues;
  const { setAction } = useContext(UnsavedChangesContext);
  const [isLoading, setIsLoading] = useState(false);
  const attemptsRef = useRef(0);

  const doReportPollCheck = useRef(false);
  const [shouldPoll, setShouldPoll] = useState(false);
  const setDoReportPollCheck = useCallback(
    (value: boolean) => {
      doReportPollCheck.current = value;
      setShouldPoll(value);
    },
    [setShouldPoll]
  );
  const [reportRequestData, setReportRequestData] = useState<TRequestData | null>(null);
  const [reportUUID, setReportUUID] = useState<string | null>(null);

  const [currentSelectedValues, setCurrentSelectedValues] = useState({});

  // Reset loading state when polling is stopped
  useEffect(() => {
    if (!shouldPoll) {
      setIsLoading(false);
    }
  }, [shouldPoll]);

  const { processErrorResponse, successNotification, infoNotification } = useResponse();

  const checkReport = useCallback(
    async (reportUUID: string, requestData: TRequestData) => {
      if (requestData && doReportPollCheck.current) {
        dispatch(globalAction.showLoadingProgress(true));

        try {
          attemptsRef.current += 1;
          const reportFileData = await pollingFn(reportUUID, requestData);
          if (reportFileData?.http_status && keepPollingStatusCodes.includes(reportFileData.http_status)) {
            setAction(false);
            setTimeout(() => {
              checkReport(reportUUID, requestData);
            }, pollInterval);
            return;
          }
          setDoReportPollCheck(false);
          successNotification(successMessage);
          setIsLoading(false);
          await onSuccessFn(reportFileData, requestData);
        } catch (error: any) {
          const errorResponse = await error.response;
          if (keepPollingStatusCodes.includes(errorResponse.status) && attemptsRef.current <= maxAttempts) {
            // the report is not ready yet
            setAction(false);
            setTimeout(() => {
              checkReport(reportUUID, requestData);
            }, pollInterval);
          } else if (errorResponse.status === Number(ERROR_404) && attemptsRef.current > maxAttempts) {
            // If we reach the max attempts (this means that we check the report along 120 seconds)
            // and the status is still 404, we show a message to the user
            // because the report is taking longer than expected and the file is not ready yet. Also
            // this could be that the report generation failed because of an error.
            // If the user see this message and after a while the report is not in the reports folder
            // that means that the report generation failed because a bug or something else.
            infoNotification(maxAttemptsMessage);
            setIsLoading(false);
          } else if (errorResponse.status === Number(ERROR_504) && attemptsRef.current > maxAttempts) {
            // If we reach the max attempts with 504 errors, show timeout message
            infoNotification(infoMessage);
            setIsLoading(false);
          } else {
            // If the error is different from 404 and 504, we show the error message to the user
            // this means a error occurred while checking the report, not in report generation
            const action = 'Excel Report';
            setDoReportPollCheck(false);
            setReportRequestData(null);
            setIsLoading(false);
            const errors = JSON.parse(error?.response?.text ?? '{}');
            processErrorResponse({
              error,
              defaultErrorMessage,
              action,
            });
            return errors;
          }
        } finally {
          dispatch(globalAction.showLoadingProgress(false));
        }
      }
    },
    [
      defaultErrorMessage,
      dispatch,
      infoMessage,
      keepPollingStatusCodes,
      maxAttemptsMessage,
      onSuccessFn,
      pollingFn,
      processErrorResponse,
      setAction,
      successMessage,
      successNotification,
      infoNotification,
      maxAttempts,
      pollInterval,
      setReportRequestData,
      setDoReportPollCheck,
    ]
  );

  useEffect(() => {
    if (doReportPollCheck && reportUUID && reportRequestData) {
      setIsLoading(true);
      attemptsRef.current = 0;
      checkReport(reportUUID, reportRequestData);
      return () => {};
    }
  }, [doReportPollCheck, reportUUID, reportRequestData, checkReport]);

  return {
    doReportPollCheck,
    reportRequestData,
    reportUUID,
    setDoReportPollCheck,
    setReportRequestData,
    setReportUUID,
    currentSelectedValues,
    setCurrentSelectedValues,
    isLoading,
  };
}

export default useCheckReportFile;
